import React, { useState, useEffect, useContext } from 'react';
import { API, AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Calendar, Clock, MapPin, CheckCircle, AlertCircle, Trash2 } from 'lucide-react';
import { format } from 'date-fns';

const Scheduling = () => {
  const { user } = useContext(AuthContext);
  const [stocktakes, setStocktakes] = useState([]);
  const [stores, setStores] = useState([]);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    store_id: '',
    scheduled_date: ''
  });
  const token = localStorage.getItem('token');
  const isNetworkAdmin = user?.role === 'network_admin';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [stocktakesRes, storesRes] = await Promise.all([
        fetch(`${API}/stocktakes`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/stores`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      const stocktakesData = await stocktakesRes.json();
      const storesData = await storesRes.json();
      
      // Sort by scheduled date
      stocktakesData.sort((a, b) => new Date(a.scheduled_date) - new Date(b.scheduled_date));
      
      setStocktakes(stocktakesData);
      setStores(storesData);
    } catch (error) {
      console.error(error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API}/stocktakes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          store_id: formData.store_id,
          scheduled_date: new Date(formData.scheduled_date).toISOString()
        })
      });
      if (response.ok) {
        toast.success('Stocktake scheduled successfully!');
        setOpen(false);
        setFormData({ store_id: '', scheduled_date: '' });
        fetchData();
      } else {
        toast.error('Failed to schedule stocktake');
      }
    } catch (error) {
      toast.error('Failed to schedule stocktake');
    }
  };

  const updateStatus = async (stocktakeId, newStatus) => {
    try {
      await fetch(`${API}/stocktakes/${stocktakeId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });
      toast.success('Status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const deleteStocktake = async (stocktakeId, storeName) => {
    if (!window.confirm(`Are you sure you want to delete the stocktake for ${storeName}? This action cannot be undone.`)) {
      return;
    }
    try {
      const response = await fetch(`${API}/stocktakes/${stocktakeId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Stocktake deleted successfully');
        fetchData();
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Failed to delete stocktake');
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete stocktake');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'planning':
        return 'bg-blue-100 text-blue-800';
      case 'in_progress':
        return 'bg-orange-100 text-orange-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-slate-100 text-slate-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'in_progress':
        return <Clock className="w-5 h-5 text-orange-500" />;
      default:
        return <AlertCircle className="w-5 h-5 text-blue-500" />;
    }
  };

  const getDaysUntil = (date) => {
    const days = Math.ceil((new Date(date) - new Date()) / (1000 * 60 * 60 * 24));
    return days;
  };

  return (
    <div className="space-y-8" data-testid="scheduling-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-slate-900 tracking-tight mb-2">Stocktake Scheduling</h1>
          <p className="text-slate-600">Plan and manage your stocktake calendar</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-teal-700 hover:bg-teal-800" data-testid="schedule-stocktake-button">
              <Plus className="w-4 h-4 mr-2" />
              Schedule Stocktake
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="schedule-stocktake-dialog">
            <DialogHeader>
              <DialogTitle>Schedule New Stocktake</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="store">Store</Label>
                <Select value={formData.store_id || ""} onValueChange={(val) => setFormData({ ...formData, store_id: val })}>
                  <SelectTrigger data-testid="store-select">
                    <SelectValue placeholder="Select store" />
                  </SelectTrigger>
                  <SelectContent>
                    {stores.map((store) => (
                      <SelectItem key={store.id} value={store.id}>
                        {store.name} ({store.code})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="date">Scheduled Date</Label>
                <Input
                  id="date"
                  type="datetime-local"
                  value={formData.scheduled_date}
                  onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
                  required
                  data-testid="date-input"
                  className="h-12"
                />
              </div>
              <Button type="submit" className="w-full bg-teal-700 hover:bg-teal-800" data-testid="submit-schedule-button">
                Schedule Stocktake
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Timeline View */}
      <div className="space-y-4">
        {stocktakes.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
            <Calendar className="w-12 h-12 text-slate-400 mx-auto mb-4" />
            <p className="text-slate-600">No stocktakes scheduled yet</p>
          </div>
        ) : (
          stocktakes.map((stocktake) => {
            const daysUntil = getDaysUntil(stocktake.scheduled_date);
            const isUpcoming = daysUntil > 0;
            const isPast = daysUntil < 0;
            
            return (
              <div
                key={stocktake.id}
                className={`bg-white rounded-xl border-2 p-6 transition-all hover:shadow-md ${
                  stocktake.status === 'completed' ? 'border-green-200' :
                  stocktake.status === 'in_progress' ? 'border-orange-200' :
                  'border-slate-200'
                }`}
                data-testid={`stocktake-${stocktake.id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start gap-4">
                    <div className="mt-1">
                      {getStatusIcon(stocktake.status)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-semibold text-slate-900">{stocktake.store_name}</h3>
                        <span className={`text-xs px-3 py-1 rounded-full font-semibold uppercase ${getStatusColor(stocktake.status)}`}>
                          {stocktake.status.replace('_', ' ')}
                        </span>
                      </div>
                      <div className="flex items-center gap-6 text-sm text-slate-600">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          <span>{new Date(stocktake.scheduled_date).toLocaleDateString('en-GB', { 
                            day: 'numeric', 
                            month: 'short', 
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          <span className={`font-medium ${
                            isUpcoming ? 'text-blue-600' : 
                            isPast ? 'text-slate-400' : 
                            'text-orange-600'
                          }`}>
                            {isUpcoming ? `${daysUntil} days away` : 
                             isPast ? `${Math.abs(daysUntil)} days ago` : 
                             'Today'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {stocktake.status === 'planning' && (
                      <Button
                        size="sm"
                        onClick={() => updateStatus(stocktake.id, 'in_progress')}
                        className="bg-orange-500 hover:bg-orange-600"
                        data-testid={`start-stocktake-${stocktake.id}`}
                      >
                        Start Count
                      </Button>
                    )}
                    {stocktake.status === 'in_progress' && (
                      <Button
                        size="sm"
                        onClick={() => updateStatus(stocktake.id, 'completed')}
                        className="bg-green-600 hover:bg-green-700"
                        data-testid={`complete-stocktake-${stocktake.id}`}
                      >
                        Mark Complete
                      </Button>
                    )}
                    {isNetworkAdmin && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => deleteStocktake(stocktake.id, stocktake.store_name)}
                        className="text-red-600 hover:bg-red-50 hover:text-red-700"
                        data-testid={`delete-stocktake-${stocktake.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="mt-4">
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="text-slate-600">Progress</span>
                    <span className="font-semibold text-slate-900">{stocktake.progress}%</span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-3 overflow-hidden">
                    <div
                      className={`h-full transition-all duration-500 ${
                        stocktake.status === 'completed' ? 'bg-green-600' :
                        stocktake.status === 'in_progress' ? 'bg-orange-500' :
                        'bg-blue-500'
                      }`}
                      style={{ width: `${stocktake.progress}%` }}
                    ></div>
                  </div>
                  <div className="flex items-center justify-between text-xs text-slate-500 mt-2">
                    <span>{stocktake.items_counted} / {stocktake.items_total} items</span>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default Scheduling;
